import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowLeft, Loader2, Music, Sparkles, Moon, Flame, Zap, Cloud, Rocket, Droplets } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import BeatPlayer from "../components/BeatPlayer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MOOD_ICONS = { Dark: Moon, Aggressive: Flame, Bouncy: Zap, Melodic: Music, Chill: Cloud, Hype: Rocket, Sad: Droplets, Ethereal: Sparkles };

const MoodBeatsPage = () => {
  const { moodName } = useParams();
  const [beats, setBeats] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState("popular");

  useEffect(() => {
    const fetchMoodBeats = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/discover/moods/${moodName}?sort_by=${sortBy}`);
        setBeats(res.data.beats || []);
        setMeta(res.data.meta);
      } catch (e) {
        console.error("Error fetching mood beats:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchMoodBeats();
  }, [moodName, sortBy]);

  const Icon = MOOD_ICONS[moodName] || Sparkles;

  return (
    <div className="min-h-screen py-10 px-6" data-testid="mood-beats-page">
      <div className="max-w-7xl mx-auto">
        <Link to="/discover" className="inline-flex items-center gap-2 text-zinc-500 hover:text-neon-cyan mb-6 transition-colors" data-testid="back-to-discover">
          <ArrowLeft className="w-4 h-4" /> Back to Discover
        </Link>

        <div className="mb-10 flex items-center gap-4">
          <div className="w-16 h-16 flex items-center justify-center" style={{ background: `${meta?.color || '#00f3ff'}22`, border: `1px solid ${meta?.color || '#00f3ff'}44` }}>
            <Icon className="w-8 h-8" style={{ color: meta?.color }} />
          </div>
          <div>
            <h1 className="font-heading text-4xl md:text-5xl uppercase tracking-tight" style={{ color: meta?.color }}>
              {moodName}
            </h1>
            <p className="text-zinc-400">{meta?.description} &mdash; {beats.length} beats</p>
          </div>
        </div>

        <div className="flex items-center justify-between mb-6">
          <Badge variant="outline" className="border-white/20">{beats.length} results</Badge>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-44 bg-black/50 border-white/10" data-testid="mood-sort">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-white/10">
              <SelectItem value="popular">Most Popular</SelectItem>
              <SelectItem value="newest">Newest</SelectItem>
              <SelectItem value="price_low">Price: Low to High</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-neon-cyan animate-spin" /></div>
        ) : beats.length === 0 ? (
          <div className="text-center py-20">
            <Music className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
            <p className="text-zinc-400">No beats found for this mood</p>
          </div>
        ) : (
          <div className="space-y-4">
            {beats.map((beat, index) => (
              <motion.div key={beat.beat_id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.03 }}>
                <BeatPlayer beat={beat} />
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MoodBeatsPage;
